import { ConfigurableProduct } from "@/types/graphql.prod"
import mapProduct from "@/util/mapProduct"
import { gql, useQuery } from "@apollo/client"
import { useMemo } from "react"
import { useIp } from "../ip-provider"

export const ProductDetailsFragment = gql`
  fragment ProductDetailsFragment on ProductInterface {
    __typename
    # eslint-disable-next-line @graphql-eslint/require-id-when-available
    categories {
      uid
      breadcrumbs {
        category_uid
      }
    }
    video_url
    description {
      html
    }
    short_description {
      html
    }
    presale_product {
      status
      label
      note
      shipping_to
      shipping_from
    }
    replacement_product {
      id
      uid
      name
      url_key
      rating_summary
      media_gallery {
        label
        url
      }
      price {
        regularPrice {
          amount {
            currency
            value
          }
        }
      }
      price_range {
        maximum_price {
          final_price {
            currency
            value
          }
          discount {
            amount_off
          }
        }
        minimum_price {
          final_price {
            currency
            value
          }
          regular_price {
            currency
            value
          }
          discount {
            amount_off
          }
        }
      }
    }
    question_answer {
      name
      author
      email
      question
      answer
      date
    }
    id
    uid
    # eslint-disable-next-line @graphql-eslint/require-id-when-available
    media_gallery_entries {
      uid
      label
      position
      disabled
      file
    }
    media_gallery_entries {
      uid
      disabled
      file
      label
      position
      url(type: "image", width: 700, height: 700)
    }
    # meta_title
    # meta_keyword
    # meta_description
    name
    price {
      regularPrice {
        amount {
          currency
          value
        }
      }
    }
    price_range {
      maximum_price {
        final_price {
          currency
          value
        }
        discount {
          amount_off
        }
        regular_price {
          currency
          value
        }
      }
      minimum_price {
        final_price {
          currency
          value
        }
        regular_price {
          currency
          value
        }
        discount {
          amount_off
        }
      }
    }
    sku
    show_attribute {
      label
      code
      value
      __typename
    }
    small_image {
      url
    }
    stock_status
    url_key
    # custom_attributes {
    #     selected_attribute_options {
    #         attribute_option {
    #             uid
    #             label
    #             is_default
    #         }
    #     }
    #     entered_attribute_value {
    #         value
    #     }
    #     attribute_metadata {
    #         uid
    #         code
    #         label
    #         attribute_labels {
    #             store_code
    #             label
    #         }
    #         data_type
    #         is_system
    #         entity_type
    #         ui_input {
    #             ui_input_type
    #             is_html_allowed
    #         }
    #         ... on ProductAttributeMetadata {
    #             used_in_components
    #         }
    #     }
    # }
    ... on ConfigurableProduct {
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      configurable_options {
        attribute_code
        attribute_id
        uid
        label
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        values {
          uid
          default_label
          label
          store_label
          use_default_value
          value_index
          swatch_data {
            ... on ImageSwatchData {
              thumbnail
            }
            value
          }
        }
      }
      variants {
        attributes {
          code
          value_index
        }
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        product {
          swatch_image
          id
          uid
          presale_product {
            status
            label
            note
            shipping_to
            shipping_from
          }
          # eslint-disable-next-line @graphql-eslint/require-id-when-available
          media_gallery_entries {
            uid
            disabled
            file
            label
            position
            url(type: "image", width: 700, height: 700)
          }
          hover_image(width: 120, height: 120)
          media_gallery {
            label
            url(type: "thumbnail", width: 120, height: 120)
            position
            disabled
          }
          sku
          stock_status
          price {
            regularPrice {
              amount {
                currency
                value
              }
            }
          }
          price_range {
            maximum_price {
              final_price {
                currency
                value
              }
              discount {
                amount_off
              }
            }
          }
        }
      }
    }
  }
`

export const GET_PRODUCT_DETAIL_QUERY = gql`
  query getProductDetailForProductPage2222($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        id
        uid
        rating_summary
        review_count
        ...ProductDetailsFragment
      }
    }
  }
  ${ProductDetailsFragment}
`

export const useProductData = ({
  newUrlKey,
  skip,
}: {
  newUrlKey: string
  skip: boolean
}) => {
  const { currentIp } = useIp()

  const { error, loading, data } = useQuery<{
    products: { items: ConfigurableProduct[] }
  }>(GET_PRODUCT_DETAIL_QUERY, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: {
      urlKey: newUrlKey,
    },
    skip: skip,
    onCompleted: async ({ products }) => {
      try {
        const productFind = products?.items?.find(
          (item: any) => item.url_key === newUrlKey
        )

        if (!productFind) {
          return null
        }

        const product = mapProduct(productFind)

        const productPrice = product?.price_range?.maximum_price

        await fetch("/api/pinterest-page-visit", {
          method: "POST",
          body: JSON.stringify({
            product,
            productPrice,
            ip: currentIp,
          }),
        })
      } catch (err) {
        console.error("Pinterest conversion error:", err)
      }

      try {
        const productFind = products.items.find(
          (item: any) => item.url_key === newUrlKey
        )

        if (!productFind) {
          return null
        }

        const product = mapProduct(productFind)

        const productPrice = product?.price_range?.maximum_price

        if (product) {
          window.dataLayer &&
            window.dataLayer.push({
              ecommerce: null,
            })
          window.dataLayer &&
            window.dataLayer.push({
              event: "view_item",
              ecommerce: {
                currency: productPrice?.final_price.currency,
                value: productPrice?.final_price.value,
                items: [
                  {
                    item_name: product?.name,
                    item_id: product?.sku,
                    price: productPrice?.final_price.value,
                    quantity: 1,
                  },
                ],
              },
            })

          window.pintrk &&
            window.pintrk("track", "pagevisit", {
              line_items: [
                {
                  product_name: product?.name,
                  product_id: product?.uid,
                  product_category: null,
                  product_price: productPrice?.final_price.value,
                  product_brand: "kingsyard",
                },
              ],
            })

          window.fbq &&
            window.fbq("track", "ViewContent", {
              content_ids: [product.sku],
              content_category: null,
              content_name: product.name,
              content_type: "product",
              contents: [
                {
                  product_name: product.name,
                  product_id: product.uid,
                  product_category: null,
                  product_price: productPrice?.final_price.value,
                  product_brand: "kingsyard",
                },
              ],
              currency: productPrice?.final_price.value,
              value: productPrice?.final_price.value,
            })
        }
      } catch (err) {
        console.error(err)
      }
    },
  })

  const product = useMemo(() => {
    if (!data) {
      // The product isn't in the cache and we don't have a response from GraphQL yet.
      return null
    }

    // Note: if a product is out of stock _and_ the backend specifies not to
    // display OOS items, the items array will be empty.

    // Only return the product that we queried for.
    const product = data.products.items.find(
      (item: any) => item.url_key === newUrlKey
    )

    if (!product) {
      return null
    }

    return mapProduct(product) as ConfigurableProduct
  }, [data, newUrlKey])

  return {
    error,
    loading,
    data,
    product,
  }
}
